import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai/react';
import type { UserId } from 'balus-common';
import { followingUserIdAtom } from './followingUserAtom';
import { viewersAtom } from '@view-model/models/sticky/viewers/viewersAtom';
import type { Viewer } from './Viewer';

type UseFollowingUserReturn = {
  /** 現在フォロー中のユーザーID */
  followingUserId: UserId | null;
  /** フォロー中のViewerオブジェクト */
  followingViewer: Viewer | null;
  /** フォロー開始 */
  startFollowing: (userId: UserId) => void;
  /** フォロー解除 */
  stopFollowing: () => void;
  /** 特定ユーザーをフォロー中か判定 */
  isFollowing: (userId: UserId) => boolean;
};

export const useFollowingUser = (): UseFollowingUserReturn => {
  const { t } = useTranslation();
  const [followingUserId, setFollowingUserId] = useAtom(followingUserIdAtom);
  const viewers = useAtomValue(viewersAtom);

  const followingViewer = followingUserId ? (viewers.find((v) => v.userId === followingUserId) ?? null) : null;

  const startFollowing = useCallback(
    (userId: UserId) => {
      setFollowingUserId(userId);
    },
    [setFollowingUserId]
  );

  const stopFollowing = useCallback(() => {
    setFollowingUserId(null);
    toast(t('canvas.viewer.unfollowed'));
  }, [setFollowingUserId, t]);

  const isFollowing = useCallback(
    (userId: UserId) => {
      return followingUserId === userId;
    },
    [followingUserId]
  );

  return {
    followingUserId,
    followingViewer,
    startFollowing,
    stopFollowing,
    isFollowing,
  };
};
